import { locale, useLang, type Lang } from './i18n';

/**
 * Display formatting — the UI edge, the only place values are shaped for humans
 * (AI_RULES §5). Money arrives as decimal strings and is formatted as strings; no
 * arithmetic happens here.
 */

const SEP: Record<Lang, { group: string; decimal: string }> = {
  en: { group: ',', decimal: '.' },
  tr: { group: '.', decimal: ',' },
};

/**
 * "10042.9777578025" → "10,042.98" (en) / "10.042,98" (tr); keeps sign; never touches
 * float math.
 */
export function money(value: string | null | undefined, decimals = 2, lang: Lang = useLang.getState().lang): string {
  if (value === null || value === undefined || value === '') return '—';
  const neg = value.startsWith('-');
  const [intRaw = '0', fracRaw = ''] = (neg ? value.slice(1) : value).split('.');
  let frac = fracRaw.padEnd(decimals + 1, '0');
  let int = intRaw;
  // Round half up on the digit after `decimals`, carrying through the string.
  const digits = (int + frac.slice(0, decimals)).split('').map(Number);
  if (Number(frac[decimals]) >= 5) {
    let i = digits.length - 1;
    while (i >= 0) {
      const d = (digits[i] ?? 0) + 1;
      if (d < 10) {
        digits[i] = d;
        break;
      }
      digits[i] = 0;
      i -= 1;
    }
    if (i < 0) digits.unshift(1);
  }
  const all = digits.join('');
  int = all.slice(0, all.length - decimals) || '0';
  frac = all.slice(all.length - decimals);
  const { group, decimal } = SEP[lang];
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  const isZero = /^0*$/.test(int) && /^0*$/.test(frac);
  return `${neg && !isZero ? '−' : ''}${grouped}${decimals > 0 ? `${decimal}${frac}` : ''}`;
}

/** Signed money with an explicit "+" for gains. */
export function signed(value: string | null | undefined, decimals = 2, lang: Lang = useLang.getState().lang): string {
  if (value === null || value === undefined) return '—';
  const m = money(value, decimals, lang);
  return m.startsWith('−') || /^[0.,]*$/.test(m) ? m : `+${m}`;
}

export const tone = (value: string | null | undefined): string =>
  value === null || value === undefined ? '' : value.startsWith('-') ? 'neg' : /^0(\.0*)?$/.test(value) ? '' : 'pos';

export function time(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(locale(), { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function duration(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const [dd, hh, mm] = useLang.getState().lang === 'tr' ? ['g', 's', 'dk'] : ['d', 'h', 'm'];
  return d > 0 ? `${String(d)}${dd} ${String(h)}${hh}` : h > 0 ? `${String(h)}${hh} ${String(m)}${mm}` : `${String(m)}${mm}`;
}
